<script setup>
import { ref, reactive, onMounted, defineEmits } from 'vue'
import { helpers, required } from '@vuelidate/validators'
import { useToast } from 'primevue/usetoast'
import dayjs from 'dayjs'
import errorHandler from '@/helpers/errorhandler'
import FxReportContainer from '@/components/FxReportContainer'
import LaporanPersediaanBarangService from '@/services/LaporanPersediaanBarangService'
import ObatService from '@/services/ObatService'
import useVuelidate from '@vuelidate/core'
import { saveAs } from 'file-saver'
import { formatDate } from '@/helpers'

const toast = useToast()
const laporanService = new LaporanPersediaanBarangService()
const obatService = new ObatService()
let dialogViewer = ref(false)
let pdfSource = ref('')
let pdfTitle = ref('PDF Viewer')
let pdfFilename = ref('')

let isLoadingPrint = ref(false)
let isLoadingExport = ref(false)
let acObat = ref([])
let acBatch = ref([])
let form = reactive({
  date_from: null,
  date_to: null,
  obat: null,
  batch: null,
  harga: 0,
})
let submitted = ref(false)

const rules = {
  obat: {
    required: helpers.withMessage('Obat harus diisi.', required),
  },
  date_from: {
    required: helpers.withMessage('Tanggal harus diisi.', required),
  },
  date_to: {
    required: helpers.withMessage('Tanggal harus diisi.', required),
  },
}

const v$ = useVuelidate(rules, form)

// eslint-disable-next-line no-unused-vars
const emit = defineEmits(['toggle'])

onMounted(async () => {})

function searchObat(e) {
  obatService
    // .get(`?filter[nama]=${e.query}&filter[status]=aktif`)
    .getByName(`?filter[nama]=${e.query}`)
    .then((res) => {
      acObat.value = res.data.data
    })
    .catch((err) => {
      errorHandler(err, 'Data Obat', toast)
    })
}

function selectObat(e) {
  const idObat = e.value.id

  //reset batch
  form.batch = null

  if (idObat !== undefined) {
    if (idObat !== null) {
      obatService
        // .get(`?filter[nama]=${e.query}&filter[status]=aktif`)
        .getBatchReport(idObat)
        .then((res) => {
          acBatch.value = res.data.data
        })
        .catch((err) => {
          errorHandler(err, 'Data Batch Obat', toast)
        })
    }
  }
}

function onExport(isFormValid) {
  submitted.value = true

  if (!isFormValid) {
    return
  }

  isLoadingExport.value = true
  const startDate = dayjs(form.date_from).format('YYYY-MM-DD')
  const endDate = dayjs(form.date_to).format('YYYY-MM-DD')
  const obatId = form.obat.id

  let batchId = null
  if (form.batch) {
    batchId = form.batch.id
  }
  const harga = form.harga

  laporanService
    .exportLaporanKartu(startDate, endDate, obatId, batchId, harga)
    .then((res) => {
      isLoadingExport.value = false
      const url = window.URL.createObjectURL(new Blob([res.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'laporan-kartu-persediaan.xlsx') //khusus ini pakai xlsx
      document.body.appendChild(link)
      link.click()
    })

    .catch((err) => {
      errorHandler(err, 'Data Laporan Kartu Persediaan', toast)
    })
    .finally(() => (isLoadingExport.value = false))
}

function onPrint(isFormValid) {
  submitted.value = true

  if (!isFormValid) {
    return
  }

  isLoadingPrint.value = true
  const startDate = dayjs(form.date_from).format('YYYY-MM-DD')
  const endDate = dayjs(form.date_to).format('YYYY-MM-DD')
  const obatId = form.obat.id

  let batchId = null
  if (form.batch) {
    batchId = form.batch.id
  }
  const harga = form.harga
  laporanService
    .printLaporanKartu(startDate, endDate, obatId, batchId, harga)
    .then((res) => {
      const blob = new Blob([res.data], { type: 'application/pdf' })
      const pdfUrl = URL.createObjectURL(blob)
      pdfSource.value = pdfUrl + '#toolbar=0'
      pdfFilename.value = 'laporan-kartu-persediaan.pdf'
      pdfTitle.value = 'PDF Viewer (' + pdfFilename.value + ')'
      dialogViewer.value = true
    })

    .catch((err) => {
      errorHandler(err, 'Data Laporan Kartu Stok ', toast)
    })
    .finally(() => (isLoadingPrint.value = false))
}

function onPrintPdf() {
  const iframe = document.createElement('iframe')
  iframe.src = pdfSource.value
  iframe.style.display = 'none'
  document.body.appendChild(iframe)

  // Use onload to make pdf preview work on firefox
  iframe.onload = () => {
    iframe.contentWindow.focus()
    iframe.contentWindow.print()
  }
}

function onDownloadPdf() {
  saveAs(pdfSource.value, pdfFilename.value)
}

function onToggle() {
  emit('toggle')
}

function resetForm() {
  form.date_from = null
  form.date_to = null
  form.obat = null
  form.harga = 0
  form.batch = null

  acBatch.value = []
}
</script>

<template>
  <fx-report-container
    title="Laporan Kartu Stok"
    :loading="isLoadingPrint"
    @toggle="onToggle"
  >
    <template #content>
      <form @submit.prevent="search(!v$.$invalid)">
        <div class="formgrid grid surface-ground m-1 py-3">
          <div class="field col-12">
            <div class="font-bold">Filter</div>
          </div>
          <div class="field col-12 md:col-6">
            <AutoComplete
              v-model="v$.obat.$model"
              :min-length="2"
              :suggestions="acObat"
              :class="{
                'p-invalid': v$.obat.$invalid && submitted,
              }"
              field="nama"
              class="w-full"
              input-class="w-full"
              placeholder="Obat"
              autofocus
              @complete="searchObat($event)"
              @item-select="selectObat"
            >
            </AutoComplete>
            <small
              v-if="
                (v$.obat.$invalid && submitted) || v$.obat.$pending.$response
              "
              class="p-error"
              >{{ v$.obat.required.$message }}</small
            >
          </div>
          <div class="field col-12 md:col-6">
            <Dropdown
              v-model="form.batch"
              :options="acBatch"
              :filter="true"
              :editable="true"
              dataKey="id"
              optionLabel="batch_no"
              class="w-full"
              input-class="w-full"
              placeholder="Batch"
              ><template #value="slotProps">
                <div class="p-dropdown-car-value" v-if="slotProps.value">
                  <span>{{ slotProps.value.batch_no }}</span>
                </div>
                <span v-else>
                  {{ slotProps.placeholder }}
                </span>
              </template>
              <template #option="slotProps">
                <div class="p-dropdown-car-option">
                  <div>
                    <strong>{{ slotProps.option.batch_no }}</strong
                    ><br />
                    Expired: {{ formatDate(slotProps.option.expired_at) }}<br />
                    Supplier: {{ slotProps.option.nama_supplier }}
                  </div>
                </div>
              </template>
            </Dropdown>
          </div>
          <div class="field col-12 md:col-2">
            <Calendar
              v-model="v$.date_from.$model"
              dateFormat="dd/mm/yy"
              :class="{
                'p-invalid': v$.date_from.$invalid && submitted,
              }"
              placeholder="Tanggal (dari)"
              class="w-full"
            />
            <small
              v-if="
                (v$.date_from.$invalid && submitted) ||
                v$.date_from.$pending.$response
              "
              class="p-error"
              >{{ v$.date_from.required.$message }}</small
            >
          </div>
          <div class="field col-12 md:col-2">
            <Calendar
              v-model="v$.date_to.$model"
              dateFormat="dd/mm/yy"
              :class="{
                'p-invalid': v$.date_to.$invalid && submitted,
              }"
              placeholder="Tanggal (s/d)"
              class="w-full"
            />
            <small
              v-if="
                (v$.date_to.$invalid && submitted) ||
                v$.date_to.$pending.$response
              "
              class="p-error"
              >{{ v$.date_to.required.$message }}</small
            >
          </div>
          <div class="field col-12 md:col-4 xl:col-2 flex xl:align-items-center">
            <div class="flex align-items-center" style="height: fit-content;">
              <InputSwitch
                v-model="form.harga"
                :true-value="1"
                :false-value="0"
              />
              <span class="text-sm ml-2">Tampilkan harga</span>
            </div>
          </div>
          <div class="field col-12 md:col-4 xl:col-5">
            <Button
              type="button"
              icon="pi pi-file-pdf"
              label="Cetak"
              class="mb-0 md:mb-2 lg:mb-0"
              @click="onPrint(!v$.$invalid)"
              :loading="isLoadingPrint"
            />
            <Button
              :loading="isLoadingExport"
              type="button"
              icon="pi pi-file-excel"
              label="Export"
              class="p-button-success ml-2 md:ml-0 lg:ml-2 mb-0 md:mb-2 lg:mb-0"
              @click="onExport(!v$.$invalid)"
            />
            <Button
              type="reset"
              icon="pi pi-refresh"
              label="Reset"
              class="p-button-outlined ml-2 md:ml-0 xl:ml-2 lg:mt-2 xl:mt-0"
              @click="resetForm"
            />
          </div>
        </div>
      </form>
    </template>
  </fx-report-container>
  <Dialog
    :header="pdfTitle"
    v-model:visible="dialogViewer"
    :breakpoints="{ '960px': '75vw', '640px': '90vw' }"
    :style="{ width: '90vw' }"
    :modal="true"
  >
    <div>
      <embed
        :src="pdfSource"
        type="application/pdf"
        width="100%"
        height="600px"
      />
    </div>
    <template #footer>
      <Button
        type="button"
        label="Download"
        icon="pi pi-download"
        class="p-button-text mr-2"
        @click="onDownloadPdf"
      />
      <Button
        type="button"
        label="Print"
        icon="pi pi-print"
        @click="onPrintPdf"
      />
    </template>
  </Dialog>
</template>
